
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useCheckRecords } from '@/hooks/useCheckRecords';
import { useTrips } from '@/hooks/useTrips';
import { Camera, Plus, MapPin, Image as ImageIcon, Grid3X3, List, Calendar, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import LoginPrompt from '@/components/shared/LoginPrompt';

const Album = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { recordsQuery, createMutation } = useCheckRecords(user?.id);
  const { tripsQuery } = useTrips(user?.id);
  const [viewMode, setViewMode] = useState('grid');
  const [dialogOpen, setDialogOpen] = useState(false);

  const [newSpot, setNewSpot] = useState('');
  const [newRemark, setNewRemark] = useState('');
  const [newTripId, setNewTripId] = useState('none');
  const [newPhotoUrls, setNewPhotoUrls] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-text-tertiary">加载中...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <Navbar />
        <div className="pt-28 pb-20">
          <LoginPrompt title="打卡相册" description="登录后记录旅途中的每一个精彩瞬间" />
        </div>
      </div>
    );
  }

  const records = recordsQuery.data || [];
  const trips = tripsQuery.data || [];

  const groupedByTrip = {};
  records.forEach((record) => {
    const key = record.trip_id || 'uncategorized';
    if (!groupedByTrip[key]) {
      const trip = trips.find((t) => t.id === record.trip_id);
      groupedByTrip[key] = {
        tripName: trip?.title || trip?.destination || '未分类',
        records: [],
      };
    }
    groupedByTrip[key].records.push(record);
  });

  const handleSubmit = async () => {
    if (!newSpot.trim()) {
      toast.error('请输入打卡地点名称');
      return;
    }
    setSubmitting(true);
    try {
      await createMutation.mateAsync({
        user_id: user.id,
        trip_id: newTripId !== 'none' ? parseInt(newTripId) : null,
        spot_name: newSpot,
        remark: newRemark,
        photo_urls: newPhotoUrls ? newPhotoUrls.split('\n').filter(Boolean) : [],
        location: {},
        check_time: new Date().toISOString(),
      });
      toast.success('打卡成功！');
      setDialogOpen(false);
      setNewSpot('');
      setNewRemark('');
      setNewTripId('none');
      setNewPhotoUrls('');
    } catch (err) {
      toast.error('打卡失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-20">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight">打卡相册</h1>
            <p className="text-text-secondary mt-2">记录旅途中的每一刻</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white rounded-full border border-[#E8E4DF] p-1 shadow-card">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-primary-light text-primary' : 'text-text-tertiary hover:text-text-secondary'}`}>
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('timeline')} className={`p-2 rounded-full transition-colors ${viewMode === 'timeline' ? 'bg-primary-light text-primary' : 'text-text-tertiary hover:text-text-secondary'}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-btn px-5 shadow-btn">
                  <Plus className="w-4 h-4 mr-2" /> 新增打卡
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-card sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-text-primary">新增打卡记录</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-text-secondary mb-1 block">打卡地点 *</label>
                    <Input placeholder="例如：大理古城" value={newSpot} onChange={(e) => setNewSpot(e.target.value)} className="rounded-smwidget border-[#E8E4DF]" />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary mb-1 block">关联行程</label>
                    <Select value={newTripId} onValueChange={setNewTripId}>
                      <SelectTrigger className="rounded-smwidget border-[#E8E4DF]">
                        <SelectValue placeholder="选择行程（可选）" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">不关联</SelectItem>
                        {trips.map((trip) => (
                          <SelectItem key={trip.id} value={String(trip.id)}>{trip.title || trip.destination}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary mb-1 block">照片链接（每行一个）</label>
                    <Textarea placeholder="https://example.com/photo1.jpg" value={newPhotoUrls} onChange={(e) => setNewPhotoUrls(e.target.value)} className="rounded-smwidget border-[#E8E4DF] min-h-[80px]" />
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary mb-1 block">游玩备注</label>
                    <Textarea placeholder="记录此刻的心情..." value={newRemark} onChange={(e) => setNewRemark(e.target.value)} className="rounded-smwidget border-[#E8E4DF] min-h-[80px]" />
                  </div>
                  <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary-hover text-white rounded-smwidget">
                    {submitting ? '提交中...' : '完成打卡'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {records.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary-light flex items-center justify-center mb-6">
              <Camera className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-text-primary mb-2">还没有打卡记录</h3>
            <p className="text-text-secondary mb-6">在旅途中拍照打卡，记录美好瞬间</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary-hover text-white rounded-btn px-6">
              <Plus className="w-4 h-4 mr-2" /> 第一次打卡
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="space-y-10">
            {Object.entries(groupedByTrip).map(([key, group]) => (
              <div key={key}>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  {group.tripName}
                  <span className="text-sm text-text-tertiary font-normal">({group.records.length} 次打卡)</span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {group.records.map((record) => (
                    <Card key={record.id} className="group border-0 shadow-card hover:shadow-hover transition-all rounded-card overflow-hidden bg-white">
                      <div className="aspect-square bg-bg-secondary relative overflow-hidden">
                        {record.photo_urls?.[0] ? (
                          <img src={record.photo_urls[0]} alt={record.spot_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-10 h-10 text-primary/30" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-text-primary truncate">{record.spot_name}</p>
                        <p className="text-xs text-text-tertiary mt-1">{format(new Date(record.check_time), 'MM/dd HH:mm')}</p>
                        {record.remark && <p className="text-xs text-text-tertiary mt-1 line-clamp-1">{record.remark}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-primary/20" />
            <div className="space-y-6">
              {records.map((record, idx) => (
                <div key={record.id} className="relative pl-12 animate-fade-in" style={{ animationDelay: `${idx * 50}ms` }}>
                  <div className="absolute left-2.5 top-2 w-3 h-3 rounded-full bg-primary border-2 border-white shadow-sm" />
                  <Card className="border-0 shadow-card rounded-card bg-white hover:shadow-hover transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {record.photo_urls?.[0] && (
                          <div className="w-20 h-20 rounded-smwidget overflow-hidden shrink-0">
                            <img src={record.photo_urls[0]} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-text-primary">{record.spot_name}</h3>
                            <span className="text-xs text-text-tertiary">{format(new Date(record.check_time), 'yyyy/MM/dd HH:mm')}</span>
                          </div>
                          {record.remark && <p className="text-sm text-text-secondary">{record.remark}</p>}
                          {record.photo_urls?.length > 1 && <p className="text-xs text-primary mt-1">{record.photo_urls.length} 张照片</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Album;

